/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Box, Grid, InputAdornment, Typography } from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import PaletteIcon from "@material-ui/icons/Palette";
import SearchIcon from "@material-ui/icons/Search";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import Brightness7Icon from "@material-ui/icons/Brightness7";
import A_TextField from "../../../../Atoms/Inputs/TextFields/A_TextField";
import {
  THEME_OPTIONS,
  THEME_PREVIEWS,
  applyTheme,
  getStoredTheme,
  getStoredMode,
  toggleMode,
  MODES,
} from "./M_ThemeSelector.helper";
import "./M_ThemeSelector.css";

const FALLBACK_PREVIEW = ["#1f2937", "#374151", "#1976d2", "#dbeafe"];

/**
 * THEME_PREVIEWS slot convention used by ThemePreviewMock:
 *   [0] = sidebar / dark surface
 *   [1] = header / mid-tone
 *   [2] = primary brand color (buttons, active)
 *   [3] = soft accent / total-row tint
 */
const ThemePreviewMock = ({ colors }) => {
  const [c0, c1, c2, c3] = colors;
  return (
    <div className="m-theme-selector__mock" aria-hidden="true">
      <div className="m-theme-selector__mock-header" style={{ backgroundColor: c1 }}>
        <span
          className="m-theme-selector__mock-dot"
          style={{ backgroundColor: c2 }}
        />
        <span
          className="m-theme-selector__mock-search"
          style={{
            backgroundColor: "rgba(255,255,255,0.18)",
            borderColor: "rgba(255,255,255,0.26)",
          }}
        />
      </div>
      <div className="m-theme-selector__mock-body">
        <div
          className="m-theme-selector__mock-sidebar"
          style={{ backgroundColor: c0 }}
        >
          <span
            className="m-theme-selector__mock-nav m-theme-selector__mock-nav--active"
            style={{ backgroundColor: c2 }}
          />
          <span
            className="m-theme-selector__mock-nav"
            style={{ backgroundColor: "rgba(255,255,255,0.16)" }}
          />
          <span
            className="m-theme-selector__mock-nav"
            style={{ backgroundColor: "rgba(255,255,255,0.10)" }}
          />
          <span
            className="m-theme-selector__mock-nav"
            style={{ backgroundColor: "rgba(255,255,255,0.10)" }}
          />
        </div>
        <div className="m-theme-selector__mock-content">
          <span className="m-theme-selector__mock-title" />
          <span
            className="m-theme-selector__mock-row"
            style={{ backgroundColor: c3 }}
          />
          <span className="m-theme-selector__mock-row m-theme-selector__mock-row--ghost" />
          <span className="m-theme-selector__mock-row m-theme-selector__mock-row--ghost" />
          <div className="m-theme-selector__mock-actions">
            <span
              className="m-theme-selector__mock-btn"
              style={{ backgroundColor: c2 }}
            />
            <span
              className="m-theme-selector__mock-btn m-theme-selector__mock-btn--outline"
              style={{ borderColor: c2, color: c2 }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

ThemePreviewMock.propTypes = {
  colors: PropTypes.arrayOf(PropTypes.string).isRequired,
};

const M_ThemeSelector = (props) => {
  const { onThemeChange } = props;
  const [activeTheme, setActiveTheme] = useState(getStoredTheme());
  const [mode, setMode] = useState(getStoredMode());
  const [query, setQuery] = useState("");
  const isDarkMode = mode === MODES.DARK;

  useEffect(() => {
    setActiveTheme(getStoredTheme());
    setMode(getStoredMode());

    const handleGlobalThemeChange = (event) => {
      const { theme, mode: nextMode } = event.detail || {};
      if (theme) {
        setActiveTheme(theme);
      }
      if (nextMode) {
        setMode(nextMode);
      }
    };

    window.addEventListener("themeChange", handleGlobalThemeChange);
    return () => window.removeEventListener("themeChange", handleGlobalThemeChange);
  }, []);

  const handleToggleMode = (event) => {
    const coords = { x: event.clientX, y: event.clientY };
    setMode(toggleMode(coords));
  };

  const handleSelect = (themeId, event) => {
    if (!themeId || themeId === activeTheme) {
      return;
    }
    const coords = { x: event.clientX, y: event.clientY };
    setActiveTheme(themeId);
    applyTheme(themeId, coords);
    if (typeof onThemeChange === "function") {
      onThemeChange(themeId);
    }
  };

  const filteredThemes = useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    if (!trimmed) {
      return THEME_OPTIONS;
    }
    return THEME_OPTIONS.filter(
      (theme) =>
        theme.label.toLowerCase().includes(trimmed) ||
        theme.value.toLowerCase().includes(trimmed)
    );
  }, [query]);

  return (
    <div className="m-theme-selector">
      <div className="m-theme-selector__hero">
        <div className="m-theme-selector__hero-icon">
          <PaletteIcon />
        </div>
        <div className="m-theme-selector__hero-copy">
          <Typography className="m-theme-selector__hero-title">
            Choose your workspace theme
          </Typography>
          <Typography className="m-theme-selector__hero-subtitle">
            Switch the full app palette instantly. Tips: Ctrl + ← / → to flip
            through themes, Ctrl + M to toggle dark / light mode.
          </Typography>
        </div>
        <button
          type="button"
          className="m-theme-selector__mode-toggle"
          onClick={handleToggleMode}
          aria-pressed={isDarkMode}
          aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
        >
          {isDarkMode ? <Brightness7Icon /> : <Brightness4Icon />}
          <span>{isDarkMode ? "Light mode" : "Dark mode"}</span>
        </button>
      </div>

      <div className="m-theme-selector__search">
        <A_TextField
          variant="standard"
          placeholder="Search themes…"
          defaultValue={query}
          inputType="value"
          fullWidth
          onChange={(event) => setQuery(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </div>

      {filteredThemes.length === 0 ? (
        <div className="m-theme-selector__empty">
          <Typography>No themes match “{query}”.</Typography>
        </div>
      ) : (
        <Grid container spacing={2} className="m-theme-selector__grid">
          {filteredThemes.map((theme) => {
            const colors = THEME_PREVIEWS[theme.value] || FALLBACK_PREVIEW;
            const isActive = activeTheme === theme.value;
            const cardClassName =
              "m-theme-selector__card" +
              (isActive ? " m-theme-selector__card--active" : "");

            return (
              <Grid item xs={12} sm={6} md={4} key={theme.value}>
                <Box
                  className={cardClassName}
                  onClick={(event) => handleSelect(theme.value, event)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      handleSelect(theme.value);
                    }
                  }}
                  aria-pressed={isActive}
                >
                  <ThemePreviewMock colors={colors} />

                  <div className="m-theme-selector__title-row">
                    <div className="m-theme-selector__title-block">
                      <Typography className="m-theme-selector__title">
                        {theme.label}
                      </Typography>
                      <Typography className="m-theme-selector__meta">
                        {theme.value}
                      </Typography>
                    </div>
                    {isActive ? (
                      <div className="m-theme-selector__active-pill">
                        <CheckCircleIcon
                          className="m-theme-selector__check"
                          fontSize="small"
                        />
                        <span>Active</span>
                      </div>
                    ) : null}
                  </div>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      )}
    </div>
  );
};

M_ThemeSelector.propTypes = {
  onThemeChange: PropTypes.func,
};

export default M_ThemeSelector;
